import type { PlayerId, RoomInfoPayload } from '../protocol-types'
import { seatStyle } from '../components/Board'

/**
 * 等人屏。
 *
 * 房间码要显示得足够大 —— 它的实际用途是念给对面听、或者截图发过去。
 * 三个人不在一起，这四个字符是唯一的会合点。
 */
interface LobbyScreenProps {
  info: RoomInfoPayload
  mySeat: PlayerId
  onStart(): void
  pending: boolean
  error: string | null
}

export function LobbyScreen({ info, mySeat, onStart, pending, error }: LobbyScreenProps) {
  const full = info.seats.length === 3
  // 只有房主（0 号座）能开局，且必须三人齐 —— 这两条服务端也会拦，
  // 这里只是别让按钮看起来能按。
  const canStart = full && mySeat === 0

  return (
    <div className="mx-auto flex min-h-full max-w-md flex-col gap-5 p-5">
      <div className="text-center">
        <p className="text-sm text-slate-400">房间码 · 念给他们或截图发过去</p>
        <p className="mt-1 font-mono text-5xl font-bold tracking-[0.2em]">{info.code}</p>
      </div>

      {error !== null && (
        <p role="alert" className="rounded-md bg-red-950/60 px-3 py-2 text-sm text-red-200">
          {error}
        </p>
      )}

      <div className="space-y-2">
        <p className="text-sm text-slate-400">已就座 {info.seats.length} / 3</p>
        {info.seats.map((s) => {
          const st = seatStyle(s.seat)
          return (
            <div
              key={s.seat}
              className="flex items-center gap-3 rounded-lg bg-slate-800 px-3 py-3"
            >
              <span className={`text-xl ${st.text}`}>{st.glyph}</span>
              <span className="flex-1 font-semibold">
                {s.displayName}
                {s.seat === mySeat && <span className="ml-2 text-xs text-slate-400">（你）</span>}
                {s.seat === 0 && <span className="ml-2 text-xs text-amber-400">房主</span>}
              </span>
              {/* 掉线要看得见：不然剩下的人会一直等一个已经不在的人 */}
              <span className={s.connected ? 'text-xs text-emerald-400' : 'text-xs text-red-400'}>
                {s.connected ? '在线' : '掉线'}
              </span>
            </div>
          )
        })}
        {Array.from({ length: 3 - info.seats.length }, (_, i) => (
          <div
            key={`empty-${i}`}
            className="rounded-lg border border-dashed border-slate-700 px-3 py-3 text-sm text-slate-500"
          >
            等人加入…
          </div>
        ))}
      </div>

      <button
        type="button"
        disabled={!canStart || pending}
        onClick={onStart}
        className="min-h-[52px] rounded-lg bg-amber-500 text-base font-bold text-slate-900 disabled:bg-slate-700 disabled:text-slate-500"
      >
        {full ? (mySeat === 0 ? '开始对局' : '等房主开局') : `还差 ${3 - info.seats.length} 人`}
      </button>
    </div>
  )
}
